import {useEffect, useState} from 'react';
import {Platform} from 'react-native';

type VisualViewportState = {
  height: number;
  offsetTop: number;
};

/**
 * Returns the visual viewport height and offset on mobile web.
 *
 * When the software keyboard opens on mobile browsers (Chrome, Safari),
 * `window.visualViewport.height` shrinks while `window.innerHeight` stays
 * unchanged. `offsetTop` tracks how much the visual viewport has scrolled
 * from the top of the layout viewport (e.g. iOS Safari pushes the page up
 * when the keyboard opens).
 *
 * Returns {height: 0, offsetTop: 0} on non-web platforms (callers should
 * fall back to screen height).
 */
export function useVisualViewportHeight(): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(() => {
    if (Platform?.OS !== 'web' || typeof window === 'undefined') return {height: 0, offsetTop: 0};
    return {
      height: window.visualViewport?.height ?? 0,
      offsetTop: window.visualViewport?.offsetTop ?? 0,
    };
  });

  useEffect(() => {
    if (Platform?.OS !== 'web' || typeof window === 'undefined') return;

    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => setState({height: vv.height, offsetTop: vv.offsetTop});

    vv.addEventListener('resize', handleResize);
    vv.addEventListener('scroll', handleResize);
    return () => {
      vv.removeEventListener('resize', handleResize);
      vv.removeEventListener('scroll', handleResize);
    };
  }, []);

  return state;
}
